import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Home, Search, ArrowRight, HelpCircle } from 'lucide-react'
import { Button } from '@/components/common/Button'
import { CATEGORY_GROUPS } from '@/utils/categories'
import { CategoryGroupIcon } from '@/components/icons/CategoryGroupIcons'
import { SEO } from '@/components/common/SEO'

export function NotFoundPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [mascotError, setMascotError] = useState(false)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/browse?q=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  return (
    <div className="min-h-[80vh] flex flex-col">
      <SEO title="Page Not Found" noIndex />
      {/* Main 404 content */}
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="container max-w-2xl text-center">
          {/* 404 illustration */}
          <div className="relative mb-8">
            <div className="text-[10rem] font-bold text-slate-100 leading-none select-none">
              404
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              {mascotError ? (
                <div className="text-6xl">🔍</div>
              ) : (
                <img
                  src="/images/tui-error.png"
                  alt="Something went wrong"
                  className="w-28 h-28 object-contain drop-shadow-lg"
                  onError={() => setMascotError(true)}
                />
              )}
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Oops! Page not found
          </h1>
          <p className="text-lg text-slate-600 mb-8 max-w-md mx-auto">
            The page you&apos;re looking for doesn&apos;t exist or has been moved.
            Let&apos;s get you back on track.
          </p>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="mb-8 max-w-md mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="search"
                placeholder="Search for listings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-12 pr-4 py-3 text-lg"
              />
            </div>
          </form>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/">
              <Button size="lg" leftIcon={<Home className="w-5 h-5" />}>
                Back to Home
              </Button>
            </Link>
            <Link to="/browse">
              <Button
                variant="secondary"
                size="lg"
                leftIcon={<Search className="w-5 h-5" />}
              >
                Browse All Listings
              </Button>
            </Link>
          </div>

          {/* Quick links */}
          <div className="border-t border-slate-200 pt-8">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
              Popular Categories
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {CATEGORY_GROUPS.slice(0, 5).map((group) => (
                <Link
                  key={group.id}
                  to={`/browse?group=${group.id}`}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-full text-sm font-medium text-slate-700 transition-colors"
                >
                  <CategoryGroupIcon iconKey={group.icon} className="w-5 h-5" />
                  <span>{group.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Help section */}
      <div className="bg-slate-50 border-t border-slate-200 py-8">
        <div className="container max-w-2xl">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <HelpCircle className="w-6 h-6 text-primary-500" />
              <div className="text-left">
                <p className="font-semibold text-slate-900">Need help?</p>
                <p className="text-sm text-slate-500">Check our FAQ or contact support</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Link to="/faq">
                <Button variant="ghost" size="sm">
                  View FAQ
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="secondary" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
